import { apiClient } from '@/shared/api/client';
import type {
  User,
  CreateUserRequest,
  LoginResponse,
  RecoverPasswordRequest,
  ResetPasswordRequest,
} from '@/shared/types';

// ============================================================================
// User Service
// ============================================================================

class UserService {
  private readonly basePath = '/users';
  private readonly authPath = '/oauth/token';
  private readonly recoverPath = '/recover-password';

  private readonly CLIENT_ID = 'nepservice';
  private readonly CLIENT_SECRET = 'nepservicesecret';
  private readonly BASIC_AUTH = btoa(`${this.CLIENT_ID}:${this.CLIENT_SECRET}`);

  async login(username: string, password: string): Promise<LoginResponse> {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    params.append('grant_type', 'password');

    return apiClient.post<LoginResponse>(this.authPath, params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Basic ${this.BASIC_AUTH}`,
      },
    } as any);
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    return apiClient.post<User>(this.basePath, userData);
  }

  async getUserById(userId: number): Promise<User> {
    return apiClient.get<User>(`${this.basePath}/${userId}`);
  }

  async updateUser(userId: number, userData: Partial<User>): Promise<User> {
    return apiClient.put<User>(`${this.basePath}/${userId}`, userData);
  }

  async requestPasswordRecovery(email: string): Promise<void> {
    const request: RecoverPasswordRequest = { email };
    return apiClient.post<void>(this.recoverPath, request);
  }

  async resetPassword(email: string, token: string, password: string): Promise<void> {
    const request: ResetPasswordRequest = { email, token, password };
    return apiClient.post<void>(`${this.recoverPath}/use`, request);
  }

  async deleteUser(userId: number): Promise<void> {
    return apiClient.delete<void>(`${this.basePath}/${userId}`);
  }
}

export const userService = new UserService();
export default userService;
